/* eslint-disable */
import { makeStyles } from '@material-ui/core';
import { useState } from 'react';
import { Formik } from 'formik';
import { useMutation } from 'react-fetching-library';
import PropTypes from 'prop-types';
import RoundedButton from 'components/Shared/RoundedButton';
import Modal from 'components/Shared/Modal';
import { formatPhoneForServer } from 'helpers/phone';
import showServerError from 'helpers/showError';
import PatientForm from '../widgets/PatientForm';
import { EMPTY_ADDRESS } from 'components/Shared/constants';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import { checkPermission } from 'helpers/checkPermissions';
import permissions from 'config/permissions';
import getFullName from 'helpers/user';
import { useDispatch, useSelector } from 'react-redux';
import PatientEditModal from '../Edit/PatientEditModal';
import { getUtcDate } from '../../../../helpers/localize';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  getTransitionWithVariable,
  updateCanShowShimmer,
} from '../../../../redux_store';
import { PATIENT_LANGUAGE } from '../../constants';
import PatientAddedModal from './PatientAddedModal/PatientAddedModal';

const useStyles = makeStyles(theme => ({
  goToListLink: {
    color: theme.colors.lightBlue,
    fontWeight: 400,
    fontSize: 15,
    textAlign: 'center',
    cursor: 'pointer',
  },
  modalBtn: {
    height: 128,
    width: 300,
    borderColor: theme.colors.lightBlue,
  },
  buttonText: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 2,
    width: 'max-content',
    color: theme.colors.blue2,
    textTransform: 'uppercase',
    paddingTop: theme.spacing(2),
  },
  modal: {
    width: '100%',
    maxWidth: 1000,
    height: '90vh',
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  createButton: {
    textTransform: 'uppercase',
    borderRadius: 12,
    marginRight: theme.spacing(2),
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 120,
    height: 44,
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(4, 3),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  actionsContainer: {
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
  },
}));

const PatientCreateModal = ({ open, handleClose, accountUuid, reloadData }) => {
  const classes = useStyles();
  const {
    en: { btn, errors, notifications, titles },
  } = useSelector(state => state.i18n);
  const [backendErrors, setBackendErrors] = useState({});
  const closeModal = () => {
    handleClose();
    setBackendErrors({});
  };
  const [openPatientEditModal, togglePatientEditModal] = useState(false);
  const [openModal, toggleModal] = useState(false);
  const [patient, setPatient] = useState({}); // trigger useOrderMetadata
  const dispatch = useDispatch();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const {
    user,
    metadata: { currentAccountPermissions, childAccountPermissions, currentAccount },
  } = useSelector(state => state.user);
  const hasPermissionForOrder = checkPermission(
    currentAccount.uuid === accountUuid ? currentAccountPermissions : childAccountPermissions,
    permissions.ORDER_CREATE
  );
  const hasPermissionForSurvey = checkPermission(
    currentAccount.uuid === accountUuid ? currentAccountPermissions : childAccountPermissions,
    permissions.ORDER_SURVEY_CREATE
  );

  const successProvidersCallback = patientInfo => {
    dispatch(
      enqueueSnackbar({
        message: getTransitionWithVariable(notifications.patientCreated, {
          name: getFullName(patientInfo),
        }),
        options: {
          variant: SNACKBAR_VARIANTS.success,
        },
      })
    );
  };

  const PatientCreateAction = values => ({
    method: 'PUT',
    endpoint: `/accounts/${accountUuid}/patients`,
    body: values,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [PatientCreateAction]);

  const createPatient = async (values, { setSubmitting }) => {
    const formattedValues = {
      ...values,
      dateOfBirth: values.dateOfBirth?.includes('Z')
        ? values.dateOfBirth
        : getUtcDate(values.dateOfBirth),
      phone: {
        ...values.phone,
        phone: formatPhoneForServer(values?.phone?.phone || ''),
      },
    };
    setBackendErrors({});

    const { payload, error, status } = await mutate(formattedValues);
    if (error) {
      setIsFailed(true);
      setTimeout(() => {
        setIsFailed(false);
      }, 2000);
      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };
      setSubmitting(false);
      switch (status) {
        case 422: {
          showServerError(dispatch, errors.validationError);
          setBackendErrors(payload.validationErrors);
          break;
        }
        default: {
          showServerError(dispatch, '', options);
          break;
        }
      }
    } else if (payload) {
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
      }, 2000);
      setPatient(payload);
      successProvidersCallback(payload);
      closeModal();
      if (reloadData) {
        dispatch(updateCanShowShimmer(true));
        reloadData();
      }
      toggleModal(hasPermissionForOrder || hasPermissionForSurvey);
    }
  };

  return (
    <>
      <Modal
        open={open}
        classes={{ paper: classes.modal }}
        title={titles.addNewPatient}
        handleClose={closeModal}
      >
        <Formik
          initialValues={{
            mrnNumber: '',
            firstName: '',
            lastName: '',
            email: '',
            dateOfBirth: null,
            genderAtBirth: '',
            hasDrugAllergies: null,
            smsOptIn: false,
            drugAllergies: '',
            address: {
              ...EMPTY_ADDRESS,
            },
            phone: {
              phone: '',
            },
            preferredPhone: {
              phone: '',
            },
            patientLanguage: PATIENT_LANGUAGE.UNKNOWN,
            race: '',
          }}
          validateOnBlur={false}
          onSubmit={createPatient}
        >
          {({ isValid, handleSubmit, dirty, isSubmitting, values }) => (
            <>
              <div className={classes.scrollableContent}>
                <PatientForm values={values} backendErrors={backendErrors} hideBHI={true} />
              </div>
              <div className={classes.actionsContainer}>
                <RoundedButton
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={closeModal}
                  className={classes.createButton}
                >
                  {btn.cancel}
                </RoundedButton>
                <RoundedButton
                  variant="contained"
                  color="primary"
                  size="medium"
                  disabled={!isValid || isSubmitting || !dirty}
                  onClick={handleSubmit}
                  isLoading={loading}
                  isSuccess={isSuccess}
                  isFailed={isFailed}
                  className={classes.createButton}
                >
                  {btn.create}
                </RoundedButton>
              </div>
            </>
          )}
        </Formik>
      </Modal>
      <PatientAddedModal
        openModal={openModal}
        toggleModal={toggleModal}
        accountUuid={accountUuid}
        patient={patient}
        togglePatientEditModal={togglePatientEditModal}
        hasPermissionForSurvey={hasPermissionForSurvey}
        hasPermissionForOrder={hasPermissionForOrder}
      />
      {patient.uuid && (
        <PatientEditModal
          open={openPatientEditModal}
          patientUuid={patient.uuid}
          accountUuid={accountUuid}
          handleClose={() => togglePatientEditModal(false)}
        />
      )}
    </>
  );
};

PatientCreateModal.propTypes = {
  accountUuid: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  reloadData: PropTypes.func.isRequired,
};

export default PatientCreateModal;
